"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useCalendarElements = void 0;
const react_1 = __importDefault(require("react"));
const date_fns_1 = require("date-fns");
const eferiado_1 = require("eferiado");
const SmCalendarDay_1 = require("./SmCalendarDay");
const Button_1 = require("../Button");
const Icon_1 = require("../Icon");
const Loading_1 = require("../Loading");
const SmCalendarFooter_1 = require("./SmCalendarFooter");
const SmCalendarHeader_1 = require("./SmCalendarHeader");
const useBasicConfiguration = () => {
    const getHolidaysOfMonth = react_1.default.useCallback((date) => {
        return (0, date_fns_1.eachDayOfInterval)({
            start: (0, date_fns_1.startOfMonth)(date),
            end: (0, date_fns_1.endOfMonth)(date),
        })
            .filter((day) => (0, eferiado_1.getHolidaysByDate)(day).nacionais.length >= 1)
            .map((day) => {
            return {
                date: day,
                name: (0, eferiado_1.getHolidaysByDate)(day).nacionais[0].nome,
            };
        });
    }, []);
    const getWeeksOfMonth = react_1.default.useCallback((date) => {
        const weeks = (0, date_fns_1.eachWeekOfInterval)({
            start: (0, date_fns_1.startOfMonth)(date),
            end: (0, date_fns_1.endOfMonth)(date),
        });
        return weeks
            .map((week, index) => {
            return (0, date_fns_1.eachDayOfInterval)({
                start: week,
                end: index === weeks.length - 1 ? (0, date_fns_1.endOfWeek)(week) : weeks[index + 1],
            });
        }) // get every day between a week and the next one
            .map((array, index) => {
            if (index !== weeks.length - 1) {
                array.pop();
            }
            return array;
        }); // rem
    }, []);
    return { getWeeksOfMonth, getHolidaysOfMonth };
};
const useCalendarControl = (value, disableFuture, disablePast, minDate, maxDate, multiCalendar, disableWeekends, disabledDates) => {
    const [rangeFlag, setRangeFlag] = react_1.default.useState(false);
    const [selectedValue, setSelectedValue] = react_1.default.useState(value || {});
    const [currentMonthView, setCurrentMonthView] = react_1.default.useState((value === null || value === void 0 ? void 0 : value.start) || new Date());
    react_1.default.useMemo(() => {
        setSelectedValue(value || selectedValue);
        setCurrentMonthView((value === null || value === void 0 ? void 0 : value.start) || new Date());
    }, [value]);
    const [dayOnHoverTrail, setDayOnHoverTrail] = react_1.default.useState();
    const order = react_1.default.useMemo(() => (rangeFlag ? 'end' : 'start'), [rangeFlag]);
    const handleChangeMonth = react_1.default.useCallback((step, callback, event) => {
        callback === null || callback === void 0 ? void 0 : callback((0, date_fns_1.addMonths)(currentMonthView, step), event);
        setCurrentMonthView((0, date_fns_1.addMonths)(currentMonthView, step));
    }, [currentMonthView]);
    const handleCalendarSelection = react_1.default.useCallback((newSelectedDate, callback, event) => {
        var _a, _b, _c, _d, _e;
        switch (order) {
            case 'start': {
                if (!multiCalendar) {
                    setSelectedValue({ start: newSelectedDate });
                    (_a = callback === null || callback === void 0 ? void 0 : callback.onSelectDate) === null || _a === void 0 ? void 0 : _a.call(callback, { start: newSelectedDate }, event);
                    break;
                }
                if (selectedValue.end &&
                    (0, date_fns_1.isBefore)(newSelectedDate, selectedValue.end)) {
                    setSelectedValue(Object.assign(Object.assign({}, selectedValue), { start: newSelectedDate }));
                    (_b = callback === null || callback === void 0 ? void 0 : callback.onSelectDate) === null || _b === void 0 ? void 0 : _b.call(callback, Object.assign(Object.assign({}, selectedValue), { start: newSelectedDate }), event);
                }
                else if ((selectedValue.end &&
                    ((0, date_fns_1.isAfter)(newSelectedDate, selectedValue.end) ||
                        (0, date_fns_1.isSameDay)(newSelectedDate, selectedValue.end))) ||
                    Object.entries(selectedValue).length === 0) {
                    setSelectedValue({ start: newSelectedDate });
                    (_c = callback === null || callback === void 0 ? void 0 : callback.onSelectDate) === null || _c === void 0 ? void 0 : _c.call(callback, { start: newSelectedDate }, event);
                }
                setRangeFlag(!rangeFlag);
                break;
            }
            case 'end': {
                if (selectedValue.start &&
                    (0, date_fns_1.isBefore)(newSelectedDate, selectedValue.start)) {
                    setSelectedValue({ start: newSelectedDate });
                    (_d = callback === null || callback === void 0 ? void 0 : callback.onSelectDate) === null || _d === void 0 ? void 0 : _d.call(callback, { start: newSelectedDate }, event);
                    break;
                }
                setSelectedValue(Object.assign(Object.assign({}, selectedValue), { end: newSelectedDate }));
                (_e = callback === null || callback === void 0 ? void 0 : callback.onSelectDate) === null || _e === void 0 ? void 0 : _e.call(callback, Object.assign(Object.assign({}, selectedValue), { end: newSelectedDate }), event);
                setRangeFlag(!rangeFlag);
                break;
            }
            default: {
                break;
            }
        }
    }, [rangeFlag, selectedValue, multiCalendar]);
    const handleDaySelection = react_1.default.useCallback((newSelectedDate, currentView, callback, event) => {
        handleCalendarSelection(newSelectedDate, callback, event);
        if (!(0, date_fns_1.isSameMonth)(newSelectedDate, currentView)) {
            handleChangeMonth(newSelectedDate.getMonth() - currentView.getMonth(), callback === null || callback === void 0 ? void 0 : callback.onMonthChange, event);
        }
    }, [selectedValue, rangeFlag, multiCalendar]);
    const handleMouseTrail = react_1.default.useCallback((day) => multiCalendar && setDayOnHoverTrail(day), [multiCalendar, dayOnHoverTrail]);
    const isDayDisabled = react_1.default.useCallback((day) => (disableFuture && (0, date_fns_1.isAfter)(day, (0, date_fns_1.startOfToday)())) ||
        (disablePast && (0, date_fns_1.isBefore)(day, (0, date_fns_1.startOfToday)())) ||
        (minDate && (0, date_fns_1.isBefore)(day, (0, date_fns_1.startOfDay)(minDate))) ||
        (maxDate && (0, date_fns_1.isAfter)(day, (0, date_fns_1.startOfDay)(maxDate))) ||
        (disableWeekends && (0, date_fns_1.isWeekend)(day)) ||
        (disabledDates === null || disabledDates === void 0 ? void 0 : disabledDates.some((date) => (0, date_fns_1.isSameDay)(day, date))), [
        disableFuture,
        disablePast,
        minDate,
        maxDate,
        disableWeekends,
        disabledDates,
    ]);
    const isDaySelected = react_1.default.useCallback((day) => {
        if (!multiCalendar &&
            selectedValue.start &&
            (0, date_fns_1.isSameDay)(selectedValue.start, day)) {
            return { status: true };
        }
        if (selectedValue.start && (0, date_fns_1.isSameDay)(selectedValue.start, day)) {
            return { status: true, order: 'start' };
        }
        if (selectedValue.end && (0, date_fns_1.isSameDay)(selectedValue.end, day)) {
            return { status: true, order: 'end' };
        }
        return { status: false };
    }, [multiCalendar, selectedValue]);
    const isDayBetween = react_1.default.useCallback((day) => {
        return (multiCalendar &&
            selectedValue.start &&
            selectedValue.end &&
            (0, date_fns_1.isBefore)(selectedValue.start, selectedValue.end) &&
            (0, date_fns_1.isWithinInterval)(day, {
                start: selectedValue.start,
                end: selectedValue.end,
            }));
    }, [multiCalendar, selectedValue]);
    const isArrowDisabled = react_1.default.useCallback((orientation) => {
        return ((orientation === 'previous' &&
            minDate &&
            ((0, date_fns_1.isSameMonth)(minDate, currentMonthView) ||
                (disablePast && (0, date_fns_1.isSameMonth)(currentMonthView, new Date())))) ||
            (orientation === 'next' &&
                maxDate &&
                ((0, date_fns_1.isSameMonth)(maxDate, currentMonthView) ||
                    (disableFuture && (0, date_fns_1.isSameMonth)(currentMonthView, new Date())))));
    }, [currentMonthView, minDate, maxDate, disableFuture, disablePast]);
    const isDayOnHoverTrail = react_1.default.useCallback((day) => {
        if (multiCalendar && dayOnHoverTrail) {
            if (order === 'start' &&
                selectedValue.start &&
                (0, date_fns_1.isBefore)(dayOnHoverTrail, selectedValue.start)) {
                return (0, date_fns_1.isWithinInterval)(day, {
                    start: dayOnHoverTrail,
                    end: selectedValue.start,
                });
            }
            if (order === 'end') {
                if (selectedValue.start &&
                    !selectedValue.end &&
                    (0, date_fns_1.isAfter)(dayOnHoverTrail, selectedValue.start)) {
                    return (0, date_fns_1.isWithinInterval)(day, {
                        start: selectedValue.start,
                        end: dayOnHoverTrail,
                    });
                }
                if (selectedValue.end &&
                    (0, date_fns_1.isAfter)(dayOnHoverTrail, selectedValue.end)) {
                    return (0, date_fns_1.isWithinInterval)(day, {
                        start: selectedValue.end,
                        end: dayOnHoverTrail,
                    });
                }
            }
        }
        return false;
    }, [multiCalendar, dayOnHoverTrail, selectedValue, order]);
    return {
        order,
        dayOnHoverTrail,
        selectedValue,
        currentMonthView,
        handleChangeMonth,
        handleDaySelection,
        handleCalendarSelection,
        handleMouseTrail,
        isDayDisabled,
        isDaySelected,
        isDayBetween,
        isArrowDisabled,
        isDayOnHoverTrail,
    };
};
const useCalendarElements = ({ value, components, multiCalendarComponents, showDaysOutsideCurrentMonth, disableFuture, disablePast, highlightToday, multiCalendar, disableWeekends, disabledDates, disableHolidays, showHolidays, loading, maxDate, minDate, locale, onSelectDate, onMonthChange, }) => {
    const { order, dayOnHoverTrail, selectedValue, currentMonthView, handleChangeMonth, handleDaySelection, handleMouseTrail, isDayDisabled, isDaySelected, isDayBetween, isArrowDisabled, isDayOnHoverTrail, } = useCalendarControl(value, disableFuture, disablePast, minDate, maxDate, multiCalendar, disableWeekends, disabledDates);
    const { getWeeksOfMonth, getHolidaysOfMonth } = useBasicConfiguration();
    const daysOfweek = react_1.default.useMemo(() => {
        const now = new Date();
        return (0, date_fns_1.eachDayOfInterval)({
            start: (0, date_fns_1.startOfWeek)(now),
            end: (0, date_fns_1.endOfWeek)(now),
        })
            .map((weekday) => {
            return {
                code: (0, date_fns_1.format)(weekday, 'EEEEEE', { locale }),
                name: (0, date_fns_1.format)(weekday, 'EEEEEEE', { locale }),
            };
        })
            .map((weekday) => (react_1.default.createElement("span", { key: `${weekday.code}`, role: "columnheader", "aria-label": weekday.name }, weekday.code)));
    }, [locale]);
    const loadingElement = react_1.default.useMemo(() => {
        return (loading &&
            ((components === null || components === void 0 ? void 0 : components.loading) || (react_1.default.createElement(Loading_1.SmLoading, { type: "tertiary", loadingSize: "large", loadingVariant: "neutral" }))));
    }, [loading]);
    const footerElement = react_1.default.useCallback((currentMonth) => {
        var _a;
        return (((_a = components === null || components === void 0 ? void 0 : components.footer) === null || _a === void 0 ? void 0 : _a.call(components, currentMonth, selectedValue, showHolidays ? getHolidaysOfMonth(currentMonth) : undefined)) ||
            (showHolidays ? (react_1.default.createElement(SmCalendarFooter_1.SmCalendarFooter, { holidays: getHolidaysOfMonth(currentMonth) })) : undefined));
    }, [components === null || components === void 0 ? void 0 : components.footer, locale, selectedValue, showHolidays]);
    const arrowElement = react_1.default.useCallback((step, orientation) => {
        var _a;
        return (react_1.default.createElement(Button_1.SmButton, { disabled: isArrowDisabled(orientation), title: orientation, onClick: (event) => handleChangeMonth(step, onMonthChange, event), icon: ((_a = components === null || components === void 0 ? void 0 : components.arrows) === null || _a === void 0 ? void 0 : _a[orientation]) || (react_1.default.createElement(Icon_1.SmIcon, { name: `navigate_${orientation}`, type: "round" })), className: "-unstyled" }));
    }, [currentMonthView, minDate, maxDate, disableFuture, disablePast]);
    const headerElement = react_1.default.useCallback((currentMonth) => {
        var _a;
        return ((_a = components === null || components === void 0 ? void 0 : components.header) === null || _a === void 0 ? void 0 : _a.call(components, currentMonth, selectedValue, {
            previous: arrowElement(-1, 'previous'),
            next: arrowElement(1, 'next'),
        })) || (react_1.default.createElement(SmCalendarHeader_1.SmCalendarHeader, { currentMonth: currentMonth, arrows: {
                previous: arrowElement(-1, 'previous'),
                next: arrowElement(1, 'next'),
            }, locale: locale }));
    }, [
        locale,
        minDate,
        maxDate,
        currentMonthView,
        selectedValue,
        disableFuture,
        disablePast,
    ]);
    const multiCalendarHeaderElement = react_1.default.useCallback((currentMonth, orientation) => {
        var _a;
        return ((_a = components === null || components === void 0 ? void 0 : components.header) === null || _a === void 0 ? void 0 : _a.call(components, currentMonth, selectedValue, {
            previous: arrowElement(-1, 'previous'),
            next: arrowElement(1, 'next'),
        })) || (react_1.default.createElement(SmCalendarHeader_1.SmCalendarHeader, { currentMonth: currentMonth, arrows: {
                [orientation]: arrowElement(orientation === 'previous' ? -1 : 1, orientation),
            }, locale: locale }));
    }, [
        locale,
        minDate,
        maxDate,
        currentMonthView,
        selectedValue,
        disableFuture,
        disablePast,
    ]);
    const blankDayElement = react_1.default.useCallback((day, currentView) => {
        return ((!showDaysOutsideCurrentMonth || multiCalendar) &&
            (0, date_fns_1.getMonth)(day) !== (0, date_fns_1.getMonth)(currentView) && (react_1.default.createElement(SmCalendarDay_1.SmCalendarDay, { key: `md-${(0, date_fns_1.format)(day, 'dd-MM', { locale })}` })));
    }, [locale, showDaysOutsideCurrentMonth]);
    const dayElement = react_1.default.useCallback((week, currentView) => {
        const holidaysOfMonth = getHolidaysOfMonth(currentView);
        return week === null || week === void 0 ? void 0 : week.map((day) => {
            const isHoliday = holidaysOfMonth.some((holiday) => (0, date_fns_1.isSameDay)(holiday.date, day));
            return (blankDayElement(day, currentView) || (react_1.default.createElement(SmCalendarDay_1.SmCalendarDay, { key: `md-${(0, date_fns_1.format)(day, 'dd-MM', { locale })}`, date: day, disabled: isDayDisabled(day) || (disableHolidays && isHoliday), selected: isDaySelected(day), between: isDayBetween(day), trail: isDayOnHoverTrail(day), onClick: (event) => handleDaySelection(day, currentView, { onSelectDate, onMonthChange }, event), onMouseLeave: () => handleMouseTrail(undefined), onMouseEnter: () => handleMouseTrail(day), renderDay: (components === null || components === void 0 ? void 0 : components.day) || undefined, today: highlightToday && (0, date_fns_1.isToday)(day), outside: (0, date_fns_1.getMonth)(day) !== (0, date_fns_1.getMonth)(currentView), holiday: showHolidays && isHoliday })));
        });
    }, [
        order,
        selectedValue,
        currentMonthView,
        dayOnHoverTrail,
        locale,
        disablePast,
        disableFuture,
        disableHolidays,
        showHolidays,
        disableWeekends,
        disabledDates,
        highlightToday,
        showDaysOutsideCurrentMonth,
        multiCalendar,
        minDate,
        maxDate,
    ]);
    const weekElement = react_1.default.useCallback((currentMonth) => {
        return getWeeksOfMonth(currentMonth).map((week) => (react_1.default.createElement("div", { key: `mw-${week[0].getMonth()}-${week[0].getDate()}`, role: "row", className: "c-calendar__gridRow -days" }, dayElement(week, currentMonth))));
    }, [
        order,
        selectedValue,
        currentMonthView,
        dayOnHoverTrail,
        locale,
        disablePast,
        disableFuture,
        showHolidays,
        disableHolidays,
        disableWeekends,
        disabledDates,
        highlightToday,
        showDaysOutsideCurrentMonth,
        multiCalendar,
        minDate,
        maxDate,
    ]);
    const calendarElement = react_1.default.useCallback((customComponents, monthsAhead) => {
        var _a, _b;
        return (react_1.default.createElement("div", { className: "c-calendar__container" }, (_a = customComponents.header) === null || _a === void 0 ? void 0 :
            _a.call(customComponents),
            react_1.default.createElement("div", { className: "c-calendar__grid", role: "grid" },
                react_1.default.createElement("div", { role: "row", className: "c-calendar__gridRow -weeks" }, daysOfweek),
                react_1.default.createElement("div", { role: "rowgroup" }, weekElement(monthsAhead
                    ? (0, date_fns_1.addMonths)(currentMonthView, monthsAhead)
                    : currentMonthView))), (_b = customComponents.footer) === null || _b === void 0 ? void 0 :
            _b.call(customComponents)));
    }, [
        order,
        selectedValue,
        currentMonthView,
        dayOnHoverTrail,
        locale,
        loading,
        disablePast,
        disableFuture,
        showHolidays,
        disableHolidays,
        disableWeekends,
        disabledDates,
        highlightToday,
        showDaysOutsideCurrentMonth,
        multiCalendar,
        minDate,
        maxDate,
    ]);
    const multiCalendarElement = react_1.default.useCallback((monthsAhead) => {
        return (multiCalendar &&
            calendarElement({
                header: () => {
                    var _a;
                    return ((_a = multiCalendarComponents === null || multiCalendarComponents === void 0 ? void 0 : multiCalendarComponents.header) === null || _a === void 0 ? void 0 : _a.call(multiCalendarComponents, (0, date_fns_1.addMonths)(currentMonthView, monthsAhead), selectedValue, {
                        previous: arrowElement(-1, 'previous'),
                        next: arrowElement(-1, 'next'),
                    })) ||
                        multiCalendarHeaderElement((0, date_fns_1.addMonths)(currentMonthView, 1), 'next');
                },
                footer: () => {
                    var _a;
                    return ((_a = multiCalendarComponents === null || multiCalendarComponents === void 0 ? void 0 : multiCalendarComponents.footer) === null || _a === void 0 ? void 0 : _a.call(multiCalendarComponents)) ||
                        footerElement((0, date_fns_1.addMonths)(currentMonthView, monthsAhead));
                },
            }, monthsAhead));
    }, [
        order,
        selectedValue,
        currentMonthView,
        dayOnHoverTrail,
        locale,
        loading,
        disablePast,
        disableFuture,
        showHolidays,
        disableHolidays,
        disableWeekends,
        disabledDates,
        highlightToday,
        showDaysOutsideCurrentMonth,
        multiCalendar,
        minDate,
        maxDate,
    ]);
    const calendarComponents = {
        loadingElement,
        footerElement,
        headerElement,
        calendarElement,
        multiCalendarHeaderElement,
        arrowElement,
        multiCalendarElement,
    };
    return { selectedValue, currentMonthView, calendarComponents };
};
exports.useCalendarElements = useCalendarElements;
//# sourceMappingURL=useCalendar.js.map